import classNames from "components/ui/classNames";
import { type ReactNode, type SVGProps } from "react";

interface VirtualOperatorKeyboardProps {
  inputRef: React.RefObject<HTMLInputElement>;
}
export function VirtualOperatorKeyboard({ inputRef }: VirtualOperatorKeyboardProps) {
  return (
    <div
      className={classNames(
        "grid items-center justify-between gap-2 gap-y-4 rounded-2xl bg-neutral-50 p-2 py-2",
        "dark:bg-slate-900",
      )}
      style={{
        gridTemplateColumns: "repeat(5, 44px)",
      }}
    >
      <VirtualOperatorKeyboardButton token=">=" inputRef={inputRef}>
        <GreaterThanEqual />
      </VirtualOperatorKeyboardButton>
      <VirtualOperatorKeyboardButton token=">" inputRef={inputRef}>
        <GreaterThan />
      </VirtualOperatorKeyboardButton>
      <VirtualOperatorKeyboardButton token="<=" inputRef={inputRef}>
        <LessThanEqual />
      </VirtualOperatorKeyboardButton>
      <VirtualOperatorKeyboardButton token="<" inputRef={inputRef}>
        <LessThan />
      </VirtualOperatorKeyboardButton>

      <VirtualOperatorKeyboardButton token="=" inputRef={inputRef}>
        <Equal />
      </VirtualOperatorKeyboardButton>
      <VirtualOperatorKeyboardButton token="<>" inputRef={inputRef}>
        <Different />
      </VirtualOperatorKeyboardButton>
      <VirtualOperatorKeyboardButton token="%" inputRef={inputRef}>
        <Percent />
      </VirtualOperatorKeyboardButton>
      <VirtualOperatorKeyboardButton token=".." inputRef={inputRef}>
        <DotDot />
      </VirtualOperatorKeyboardButton>
      <VirtualOperatorKeyboardButton token="m" inputRef={inputRef}>
        <MLetter />
      </VirtualOperatorKeyboardButton>
      <VirtualOperatorKeyboardButton token="b" inputRef={inputRef}>
        <BLetter />
      </VirtualOperatorKeyboardButton>
    </div>
  );
}

interface VirtualOperatorKeyboardButtonProps {
  token: ">=" | ">" | "=" | "<=" | "<" | "%" | "!=" | "<>" | ".." | "m" | "b";
  inputRef: React.RefObject<HTMLInputElement>;
  children: ReactNode;
}
function VirtualOperatorKeyboardButton({ token, inputRef, children }: VirtualOperatorKeyboardButtonProps) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (inputRef.current) {
          const input = inputRef.current;
          const start = input.selectionStart ?? 0;
          const end = input.selectionEnd ?? 0;
          // eslint-disable-next-line prefer-destructuring
          const value = input.value;
          input.value = value.slice(0, start) + token + value.slice(end);
          input.setSelectionRange(start + token.length, start + token.length);
          input.focus();
        }
      }}
      className={classNames(
        "flex h-11 items-center justify-center rounded-2xl ring-1 ring-inset",
        "bg-slate-50 text-slate-600 ring-slate-300/70",
        "focus:outline-none",
        "hover:bg-indigo-100 hover:text-indigo-700 hover:ring-indigo-200 focus-visible:bg-orange-100 focus-visible:text-orange-700 focus-visible:ring-orange-300/50",
        "dark:bg-sky-500/40 dark:text-sky-100 dark:ring-transparent",
        "dark:hover:bg-lime-800 dark:hover:text-lime-400",
        "dark:focus-visible:bg-yellow-400/30 dark:focus-visible:text-yellow-300",
      )}
    >
      {children}
    </button>
  );
}

const GreaterThanEqual = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={21} height={20} {...props} viewBox="0 0 21 20">
    <g fill="currentColor">
      <path d="M9.96 19.922c5.499 0 9.962-4.463 9.962-9.961S15.459 0 9.96 0 0 4.463 0 9.96c0 5.499 4.463 9.962 9.96 9.962Zm0-1.66c-4.589 0-8.3-3.711-8.3-8.301s3.711-8.3 8.3-8.3c4.59 0 8.302 3.71 8.302 8.3 0 4.59-3.711 8.3-8.301 8.3Z" />
      <path d="M13.564 13.985a.717.717 0 0 0-.722-.704H7.197c-.4 0-.713.323-.713.704a.73.73 0 0 0 .713.712h5.645c.4 0 .722-.351.722-.712ZM7.53 11.777c.108 0 .225-.029.45-.126l4.843-2.168c.469-.196.635-.499.635-.918 0-.381-.176-.704-.635-.918L7.978 5.488c-.224-.097-.341-.117-.449-.117a.703.703 0 0 0-.722.694c0 .283.166.546.449.683l4.092 1.807v.03L7.256 10.4a.719.719 0 0 0-.45.673.71.71 0 0 0 .723.703Z" />
    </g>
  </svg>
);

const GreaterThan = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={21} height={20} {...props} viewBox="0 0 21 20">
    <g fill="currentColor">
      <path d="M9.96 19.922c5.499 0 9.962-4.463 9.962-9.961S15.459 0 9.96 0 0 4.463 0 9.96c0 5.499 4.463 9.962 9.96 9.962Zm0-1.66c-4.589 0-8.3-3.711-8.3-8.301s3.711-8.3 8.3-8.3c4.59 0 8.302 3.71 8.302 8.3 0 4.59-3.711 8.3-8.301 8.3Z" />
      <path d="M7.422 13.955c.195 0 .332-.039.469-.107l5.82-3.027c.41-.215.586-.46.586-.86 0-.371-.196-.644-.586-.86L7.89 5.968a.927.927 0 0 0-.489-.117c-.449 0-.79.332-.79.771 0 .362.175.596.575.791l4.942 2.49v.06l-4.942 2.43c-.41.196-.576.411-.576.782 0 .45.352.781.81.781Z" />
    </g>
  </svg>
);

const Equal = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={21} height={20} {...props} viewBox="0 0 21 20">
    <g fill="currentColor">
      <path d="M9.96 19.932c5.499 0 9.962-4.463 9.962-9.961S15.459.01 9.96.01 0 4.473 0 9.97c0 5.499 4.463 9.962 9.96 9.962Zm0-1.66c-4.589 0-8.3-3.711-8.3-8.301s3.711-8.3 8.3-8.3c4.59 0 8.302 3.71 8.302 8.3 0 4.59-3.711 8.3-8.301 8.3Z" />
      <path d="M6.27 12.53h7.353c.498 0 .85-.244.85-.733 0-.498-.332-.752-.85-.752H6.269c-.517 0-.85.254-.85.752 0 .489.352.733.85.733Zm0-3.604h7.353c.498 0 .85-.244.85-.742s-.332-.752-.85-.752H6.269c-.517 0-.85.254-.85.752s.352.742.85.742Z" />
    </g>
  </svg>
);

const LessThanEqual = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} {...props} viewBox="0 0 20 20">
    <g fill="currentColor">
      <path d="M9.96 19.922c5.499 0 9.962-4.463 9.962-9.961S15.459 0 9.96 0 0 4.463 0 9.96c0 5.499 4.463 9.962 9.96 9.962Zm0-1.66c-4.589 0-8.3-3.711-8.3-8.301s3.711-8.3 8.3-8.3c4.59 0 8.302 3.71 8.302 8.3 0 4.59-3.711 8.3-8.301 8.3Z" />
      <path d="M6.162 13.984c0 .362.322.713.723.713h5.644a.73.73 0 0 0 .713-.713.709.709 0 0 0-.713-.703H6.885c-.4 0-.723.323-.723.703Zm6.035-2.207a.71.71 0 0 0 .723-.703.719.719 0 0 0-.45-.674L8.38 8.584v-.03l4.092-1.806c.283-.137.449-.4.449-.684a.703.703 0 0 0-.723-.693c-.107 0-.224.02-.449.117L6.904 7.647c-.459.214-.634.537-.634.917 0 .42.166.723.634.918l4.844 2.168c.225.098.342.127.45.127Z" />
    </g>
  </svg>
);

const LessThan = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={21} height={20} {...props} viewBox="0 0 21 20">
    <g fill="currentColor">
      <path d="M9.96 19.922c5.499 0 9.962-4.463 9.962-9.961S15.459 0 9.96 0 0 4.463 0 9.96c0 5.499 4.463 9.962 9.96 9.962Zm0-1.66c-4.589 0-8.3-3.711-8.3-8.301s3.711-8.3 8.3-8.3c4.59 0 8.302 3.71 8.302 8.3 0 4.59-3.711 8.3-8.301 8.3Z" />
      <path d="M12.47 13.955c.47 0 .811-.332.811-.78 0-.372-.156-.587-.566-.782L7.764 9.96v-.058l4.95-2.49c.401-.196.567-.43.567-.792 0-.44-.332-.771-.79-.771a.951.951 0 0 0-.49.117l-5.82 3.135c-.39.215-.576.488-.576.86 0 .4.166.644.577.859l5.82 3.027c.146.068.273.107.469.107Z" />
    </g>
  </svg>
);

const Percent = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={19} height={21} {...props} viewBox="0 0 19 21">
    <path
      d="M3.503 9.64c2.182 0 3.495-1.83 3.495-4.742 0-2.92-1.322-4.726-3.495-4.726S0 1.986 0 4.898C0 7.818 1.322 9.64 3.503 9.64Zm0-1.253c-1.297 0-2.068-1.301-2.068-3.489 0-2.172.779-3.481 2.068-3.481 1.29 0 2.068 1.293 2.068 3.481 0 2.188-.77 3.49-2.068 3.49Zm11.702 11.46c2.181 0 3.495-1.83 3.495-4.741 0-2.92-1.322-4.726-3.495-4.726s-3.503 1.814-3.503 4.726c0 2.92 1.321 4.742 3.503 4.742Zm0-1.244c-1.298 0-2.068-1.301-2.068-3.497 0-2.172.778-3.473 2.068-3.473 1.281 0 2.06 1.293 2.06 3.473 0 2.196-.77 3.497-2.06 3.497ZM2.798 19.888a.818.818 0 0 0 1.086-.244L16.073 1.205c.308-.439.13-.903-.203-1.098-.308-.179-.754-.13-1.054.31L2.619 18.806c-.308.447-.162.886.179 1.081Z"
      fill="currentColor"
    />
  </svg>
);

const Different = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={21} height={20} {...props} viewBox="0 0 21 20">
    <g fill="currentColor">
      <path d="M9.96 19.922c5.499 0 9.962-4.463 9.962-9.961S15.459 0 9.96 0 0 4.463 0 9.96c0 5.499 4.463 9.962 9.96 9.962Zm0-1.66c-4.589 0-8.3-3.711-8.3-8.301s3.711-8.3 8.3-8.3c4.59 0 8.302 3.71 8.302 8.3 0 4.59-3.711 8.3-8.301 8.3Z" />
      <path d="M6.27 12.52h7.353c.498 0 .85-.244.85-.733 0-.498-.332-.752-.85-.752H6.269c-.517 0-.85.254-.85.752 0 .489.352.733.85.733Zm0-3.604h7.353c.498 0 .85-.244.85-.742s-.332-.752-.85-.752H6.269c-.517 0-.85.254-.85.752s.352.742.85.742Zm.41 5.537c.39.293.81.157 1.103-.244L13.31 6.65c.303-.41.313-.83-.087-1.123-.4-.293-.801-.185-1.104.235L6.572 13.32c-.303.42-.293.84.108 1.133Z" />
    </g>
  </svg>
);

const DotDot = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} {...props} viewBox="0 0 20 20">
    <g fill="none">
      <path
        fill="currentColor"
        d="M14.275 12.56a2.274 2.274 0 0 0 2.286-2.275A2.276 2.276 0 0 0 14.275 8 2.274 2.274 0 0 0 12 10.285a2.271 2.271 0 0 0 2.275 2.276Zm-8.99 0a2.28 2.28 0 1 0 0-4.56A2.276 2.276 0 0 0 3 10.285a2.274 2.274 0 0 0 2.285 2.276Z"
      />
      <path d="M0 0h20v20H0z" />
    </g>
  </svg>
);

const MLetter = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} {...props} viewBox="0 0 26 26">
    <path
      fill="currentColor"
      d="M12.715 25.44c7.021 0 12.724-5.694 12.724-12.715C25.44 5.703 19.736 0 12.715 0 5.693 0 0 5.703 0 12.725c0 7.021 5.693 12.714 12.715 12.714Zm0-1.817A10.89 10.89 0 0 1 1.816 12.725 10.89 10.89 0 0 1 12.715 1.826a10.89 10.89 0 0 1 10.898 10.899 10.89 10.89 0 0 1-10.898 10.898Z"
    />
    <path
      fill="currentColor"
      d="M7.842 18.525c.498 0 .79-.322.79-.878V9.805h.098l3.106 7.812c.166.4.44.586.86.586s.702-.185.869-.586l3.115-7.812h.097v7.841c0 .547.293.88.772.88.498 0 .8-.323.8-.88V7.822c0-.605-.439-1.015-1.044-1.015-.537 0-.899.234-1.094.732l-3.467 8.711h-.068L9.199 7.54c-.205-.499-.556-.733-1.094-.733-.615 0-1.044.41-1.044 1.015v9.825c0 .546.293.878.78.878Z"
    />
  </svg>
);

const BLetter = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} {...props} viewBox="0 0 26 26">
    <path
      fill="currentColor"
      d="M12.715 25.44c7.021 0 12.724-5.694 12.724-12.715C25.44 5.703 19.736 0 12.715 0 5.693 0 0 5.703 0 12.725c0 7.021 5.693 12.714 12.715 12.714Zm0-1.817A10.89 10.89 0 0 1 1.816 12.725 10.89 10.89 0 0 1 12.715 1.826a10.89 10.89 0 0 1 10.898 10.899 10.89 10.89 0 0 1-10.898 10.898Z"
    />
    <path
      fill="currentColor"
      d="M9.62 18.37h3.68c2.56 0 4.19-1.241 4.19-3.214 0-1.523-1.015-2.607-2.656-2.812v-.069c1.21-.293 1.973-1.298 1.973-2.548 0-1.7-1.387-2.823-3.496-2.823H9.618c-.596 0-.947.352-.947.957v9.541c0 .606.351.967.947.967Zm.732-6.524V8.203h2.617c1.367 0 2.178.674 2.178 1.797 0 1.152-.977 1.846-2.647 1.846h-2.148Zm0 5.215v-3.985h2.88c1.583 0 2.52.752 2.52 2.031 0 1.319-1.006 1.954-3.135 1.954h-2.265Z"
    />
  </svg>
);
